/*----------------------------------
- HOME: DEMO WINDOW
----------------------------------*/

/**
 * Home's product tour (`section.demo`), half in the hero card: tab 1 "Find a name" is the real public table in the
 * Browse frame, tabs 2 to 5 the sample screenshots per persona (`demoScreens.ts`), each loaded the first time its tab
 * opens (the part's `mountedScreens`). The persona is the page's (`useHomePersona`), shared with the closing card.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0028
 * @adr  ADR-0110
 * @rule An unready preview shows the kit EmptyState in tab 1 with a link to `/domains`: Home never draws fallback rows.
 * @rule No auto-advance: the window stays on the tab the visitor picked.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { EmptyState } from '@uniquedomains/components/kit/empty-state/EmptyState';
import { TextLink } from '@uniquedomains/components/kit/text-link/TextLink';
import type { DemoCallout, DemoCalloutLayers } from '@uniquedomains/components/marketing/demo-window/DemoCallouts';
import { DemoWindow, type DemoSampleTab, type DemoTab } from '@uniquedomains/components/marketing/demo-window/DemoWindow';
import type { HomePersona } from '@uniquedomains/components/marketing/demo-window/PersonaSwitch';

import Icon from '@/client/components/ui/icon/Icon';
import { HOME_DEMO_COPY, HOME_DEMO_TABS, type HomeDemoTab } from '@/client/catalogs/landing/home/demo';
import { HOME_DEMO_CALLOUTS, HOME_DEMO_TAG_AT_BOTTOM, type HomeDemoCallout, type HomeDemoCalloutLayers } from '@/client/catalogs/landing/home/demoCallouts';
import { HOME_BROWSE_COPY } from '@/client/catalogs/landing/home/homeCopy';
import type { HomeDomainsPreviewData } from '@/client/pages/landing/lib/homeDomainsPreview';
import { WEBSITE_DOMAINS_BASE_PATH } from '@/common/libs/filters';
import { HOME_DEMO_SCREENS } from './demoScreens';
import HomeBrowsePanel from './HomeBrowsePanel';

/*----------------------------------
- TYPES
----------------------------------*/

type HomeDemoProps = {
    preview: HomeDomainsPreviewData | null;
    totalDomains: number;
    persona: HomePersona;
    onPersonaChange: (persona: HomePersona) => void;
};

/*----------------------------------
- HELPERS
----------------------------------*/

const toDemoTab = (tab: HomeDemoTab): DemoTab => ({
    id: tab.id,
    label: tab.label,
    icon: <Icon name={tab.icon} size={15} />,
    testId: `home-demo-tab-${tab.id}`,
});

const [LIVE_TAB, ...SAMPLE_TAB_COPY] = HOME_DEMO_TABS;

/** A catalog layer's callouts with their sprite icons (the marketing part takes the icon as a node). */
const toCallouts = (callouts: readonly HomeDemoCallout[] | undefined): DemoCallout[] | undefined =>
    callouts?.map(({ icon, ...callout }) => ({ ...callout, icon: <Icon name={icon} size={13} strokeWidth={2.2} /> }));

const toCalloutLayers = (layers: HomeDemoCalloutLayers): DemoCalloutLayers => ({
    shared: toCallouts(layers.shared),
    investor: toCallouts(layers.investor),
    founder: toCallouts(layers.founder),
});

/** Tabs 2 to 5 with their screenshots and sample callouts; the catalog's order is the window's. */
const SAMPLE_TABS: DemoSampleTab[] = SAMPLE_TAB_COPY.flatMap((tab) =>
    tab.id === 'find' ? [] : [{
        ...toDemoTab(tab),
        screens: HOME_DEMO_SCREENS[tab.id],
        callouts: toCalloutLayers(HOME_DEMO_CALLOUTS[tab.id]),
        tagAt: HOME_DEMO_TAG_AT_BOTTOM.has(tab.id) ? 'bottom' : 'top',
    }],
);

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeDemo = ({ preview, totalDomains, persona, onPersonaChange }: HomeDemoProps) => {
    const unready = HOME_BROWSE_COPY.unready;
    const live = preview ? (
        <HomeBrowsePanel preview={preview} totalDomains={totalDomains} />
    ) : (
        <EmptyState
            title={unready.title}
            text={unready.text}
            action={<TextLink arrow href={WEBSITE_DOMAINS_BASE_PATH} label={unready.action} data-testid="home-demo-unready-link" />}
            data-testid="home-demo-unready"
        />
    );

    return (
        <DemoWindow
            id="demo"
            label={HOME_DEMO_COPY.sectionLabel}
            tabsLabel={HOME_DEMO_COPY.tabsLabel}
            liveTab={toDemoTab(LIVE_TAB)}
            live={live}
            sampleTabs={SAMPLE_TABS}
            sampleTag={HOME_DEMO_COPY.sampleTag}
            persona={persona}
            onPersonaChange={onPersonaChange}
            personaLabels={HOME_DEMO_COPY.personaLabels}
            personaLabel={HOME_DEMO_COPY.personaLabel}
        />
    );
};

export default HomeDemo;
